import {
  BasketSummaryFilterRequest,
  BasketSummaryItem,
  BasketSummaryMonthlyContent,
  BasketSummarySubjectType,
} from '@api/api/basket-summary';
import { uniqueBy } from '@helpers/utils/arrays/arrays';
import { getTimeTillEndOfDayMs } from '@helpers/utils/dates/getTimeTillEndOfDayMs';
import { useQuery } from '@tanstack/react-query';
import { toCompanyRef } from '@terminal/react-query-hooks/themes/use-pulse-table/usePulseTables.utils';
import {
  findBasketSummary,
  postBasketSummaryFilterByVersion,
  QUERY_KEY_BASKET_SUMMARY_FILTER,
} from '@terminal/services/basket-summary/basket-summary-service';
import { useFlags } from '@terminal/stores/use-flags/useFlags';
import {
  PulseDailyUpdateResult,
  PulseMonthlyDriver,
  PulseMonthlySummaries,
} from '../pulse-types';

const PULSE_SUMMARY_TYPES = ['daily', 'monthly'] as const;

const getPulseSummaryBody = ({
  id,
  summaryType,
  subjectType,
}: {
  id: string;
  summaryType: (typeof PULSE_SUMMARY_TYPES)[number];
  subjectType: BasketSummarySubjectType;
}): BasketSummaryFilterRequest => ({
  subjects: [{ type: subjectType, id }],
  summary_types: [summaryType],
  page_size: 1,
});

const getPulseSummaryQueryOptions = ({
  id,
  summaryType,
  subjectType,
  isV2,
}: {
  id: string;
  summaryType: (typeof PULSE_SUMMARY_TYPES)[number];
  subjectType: BasketSummarySubjectType;
  isV2: boolean;
}) => ({
  queryKey: [QUERY_KEY_BASKET_SUMMARY_FILTER, id, summaryType, subjectType],
  enabled: !!id,
  staleTime: getTimeTillEndOfDayMs(),
  queryFn: ({ signal }: { signal: AbortSignal }) =>
    postBasketSummaryFilterByVersion({
      body: getPulseSummaryBody({ id, summaryType, subjectType }),
      isV2,
      signal,
    }),
});

export const toPulseDailyUpdate = (
  summary: BasketSummaryItem | undefined
): PulseDailyUpdateResult | undefined => {
  if (summary?.summary_type !== 'daily' || !summary.content) {
    return undefined;
  }

  return {
    content: summary.content,
    createdAt: summary.created_at ?? undefined,
  };
};

export const usePulseDailyUpdate = ({
  id,
  subjectType = 'theme',
}: {
  id: string;
  subjectType?: BasketSummarySubjectType;
}) => {
  const isV2 = useFlags(state =>
    state.getFeatureFlagState('KG-976-basket-summary-v2')
  );

  return useQuery({
    ...getPulseSummaryQueryOptions({
      id,
      summaryType: 'daily',
      subjectType,
      isV2,
    }),
    select: summaries =>
      toPulseDailyUpdate(
        findBasketSummary({
          summaries,
          subjectId: id,
          summaryType: 'daily',
        })
      ),
  });
};

export const toCitationDrivers = (
  citations: BasketSummaryMonthlyContent['bullish']['citations']
): PulseMonthlyDriver[] =>
  uniqueBy(
    (citations ?? []).map(citation => ({
      ...toCompanyRef(citation.entity_tag),
      insightId: (citation.kg_insight_id ??
        citation.scenario_insight_id) as string,
    })),
    'entityTag'
  );

export const usePulseMonthlySummaries = ({
  id,
  subjectType = 'theme',
}: {
  id: string;
  subjectType?: BasketSummarySubjectType;
}) => {
  const isV2 = useFlags(state =>
    state.getFeatureFlagState('KG-976-basket-summary-v2')
  );

  return useQuery({
    ...getPulseSummaryQueryOptions({
      id,
      summaryType: 'monthly',
      subjectType,
      isV2,
    }),
    select: summaries => {
      const summary = findBasketSummary({
        summaries,
        subjectId: id,
        summaryType: 'monthly',
      });

      if (summary?.summary_type !== 'monthly' || !summary.content) {
        return undefined;
      }

      const tailwindCitations = summary.content.bullish.citations ?? [];
      const headwindCitations = summary.content.bearish.citations ?? [];

      const data: PulseMonthlySummaries = {
        tailwind: {
          narrative: summary.content.bullish.summary,
          citations: tailwindCitations,
          drivenBy: toCitationDrivers(tailwindCitations),
        },
        headwind: {
          narrative: summary.content.bearish.summary,
          citations: headwindCitations,
          drivenBy: toCitationDrivers(headwindCitations),
        },
      };

      return data;
    },
  });
};
